import {Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis} from 'recharts';
import {type ReactNode} from 'react';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent
} from '@kroll-resolver-crisp/ui-components-react';
import {ChartCard} from './ChartCard';
import {formatAxisValue} from './format';
import {type ReportSeriesChartBlock} from '@/api/reporting';

// The design system's chart-1..5 ramp; the contract caps a chart at 8 series, and past five the
// app's categorical chart-cat-1..8 palette takes over by slot, as DeliveryCharts does, so series
// six to eight never repeat one to three.
const SEQUENTIAL_SERIES_LIMIT = 5;
const CATEGORICAL_SERIES_COUNT = 8;

export function seriesColor(index: number, seriesCount: number): string {
  if (seriesCount <= SEQUENTIAL_SERIES_LIMIT) {
    return `var(--chart-${index + 1})`;
  }

  return `var(--chart-cat-${(index % CATEGORICAL_SERIES_COUNT) + 1})`;
}

// Line, bar and scatter are the same data under the same axes, grid, tooltip and legend; only the
// mark differs, so the three views are one component and `block.type` picks the mark.
// Scatter is the line chart with the line hidden, so the x axis stays categorical.
function lineMark(type: ReportSeriesChartBlock['type'], field: string) {
  return type === 'scatter_chart'
    ? {strokeWidth: 0, dot: {r: 3, fill: `var(--color-${field})`}, activeDot: {r: 5}}
    : {strokeWidth: 2, dot: false as const};
}

export function SeriesChartBlock({
  block,
  actions
}: {
  readonly block: ReportSeriesChartBlock;
  readonly actions?: ReactNode;
}) {
  // Series fields are contract identifiers ([a-z0-9_]), so they are safe as the config keys that
  // ChartContainer turns into `--color-<key>` custom properties.
  const config: ChartConfig = Object.fromEntries(
    block.series.map((series, i) => [
      series.field,
      {label: series.label, color: seriesColor(i, block.series.length)}
    ])
  );

  const shared = (
    <>
      <CartesianGrid vertical={false} />
      <XAxis
        dataKey={block.x.field}
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        tickFormatter={formatAxisValue}
        interval={Math.ceil(block.rows.length / 10) - 1}
      />
      <YAxis tickLine={false} axisLine={false} width={40} />
      <ChartTooltip
        content={<ChartTooltipContent labelFormatter={(label) => formatAxisValue(label)} />}
      />
      {block.series.length > 1 ? <ChartLegend content={<ChartLegendContent />} /> : null}
    </>
  );
  const margin = {top: 8, right: 8, bottom: 0, left: 0};

  return (
    <ChartCard block={block} actions={actions} empty={block.rows.length === 0}>
      <ChartContainer config={config} className="aspect-auto h-72 w-full">
        {block.type === 'bar_chart' ? (
          <BarChart accessibilityLayer data={block.rows} margin={margin}>
            {shared}
            {block.series.map((series) => (
              <Bar
                key={series.field}
                dataKey={series.field}
                fill={`var(--color-${series.field})`}
                {...(block.stacked ? {stackId: 'stack'} : {})}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        ) : (
          <LineChart accessibilityLayer data={block.rows} margin={margin}>
            {shared}
            {block.series.map((series) => (
              <Line
                key={series.field}
                type="monotone"
                dataKey={series.field}
                stroke={`var(--color-${series.field})`}
                {...lineMark(block.type, series.field)}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )}
      </ChartContainer>
    </ChartCard>
  );
}
